/**
 * Lightweight starfield with parallax-on-pointer.
 * Renders to a fixed canvas behind everything. ~zero deps.
 */
type Star = { x: number; y: number; z: number; r: number; tw: number; phase: number };

export function mountStarfield(host: HTMLElement): () => void {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const canvas = document.createElement('canvas');
  canvas.className = 'starfield';
  host.prepend(canvas);
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return () => canvas.remove();

  let w = 0, h = 0, dpr = 1;
  let stars: Star[] = [];
  let mx = 0, my = 0;
  let raf = 0;

  const PALETTE = ['#ffffff', '#cfe4ff', '#ffd9a8', '#a6c3df'];

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

    const density = Math.min(0.00012, 0.00018);
    const count = Math.max(80, Math.floor(w * h * density));
    stars = Array.from({ length: count }, (): Star => ({
      x: Math.random() * w,
      y: Math.random() * h,
      z: Math.random() * 0.9 + 0.1,
      r: Math.random() * 1.3 + 0.2,
      tw: Math.random() * 0.6 + 0.2,
      phase: Math.random() * Math.PI * 2,
    }));
  }

  function onMove(e: PointerEvent) {
    mx = (e.clientX / w - 0.5) * 2;
    my = (e.clientY / h - 0.5) * 2;
  }

  function frame(t: number) {
    ctx!.clearRect(0, 0, w, h);
    const time = t * 0.001;
    for (const s of stars) {
      const px = s.x + mx * 18 * s.z;
      const py = s.y + my * 18 * s.z;
      const tw = 0.5 + 0.5 * Math.sin(time * s.tw + s.phase);
      const alpha = 0.25 + 0.75 * tw * s.z;
      ctx!.beginPath();
      ctx!.fillStyle = PALETTE[(px | 0) % PALETTE.length];
      ctx!.globalAlpha = alpha;
      ctx!.arc(px, py, s.r * (0.6 + s.z * 0.6), 0, Math.PI * 2);
      ctx!.fill();
    }
    ctx!.globalAlpha = 1;
    raf = requestAnimationFrame(frame);
  }

  resize();
  window.addEventListener('resize', resize, { passive: true });
  if (!prefersReduced) {
    window.addEventListener('pointermove', onMove, { passive: true });
    raf = requestAnimationFrame(frame);
  } else {
    // Single static draw
    frame(0);
    cancelAnimationFrame(raf);
  }

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    window.removeEventListener('pointermove', onMove);
    canvas.remove();
  };
}
